import { Button } from "@/components/ui/baseUi/button"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleRights } from "@modules/programBundle/hooks/useProgramBundleRights"
import { useProgramBundleSubscription } from "@modules/programBundle/hooks/useProgramBundleSubscription"
import { HuProgramBundleMiniCard } from "@modules/ui/components/cards/huProgramBundleMiniCard"
import { HuProgramMiniCards } from "@modules/ui/components/cards/huProgramMiniCard"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { useMemo } from "react"
import { Text } from "@/components/ui/baseUi/text"

type ProgramBundleInfoProgramsProps = {
  programBundle: ProgramBundleEntity
  className?: string
} & React.ComponentProps<"div">

export function ProgramBundleInfoPrograms({ programBundle, className, ...props }: ProgramBundleInfoProgramsProps) {
  const {
    data: programBundleSubscriber,
    isLoading,
    followProgamsInProgramBundle,
    unFollowProgamsInProgramBundle,
  } = useProgramBundleSubscription(programBundle.id)
  const { isAuthor } = useProgramBundleRights(programBundle.id)

  const canUserFollowBundlePrograms = useMemo(
    () => programBundleSubscriber?.canUserFollowBundlePrograms(),
    [programBundleSubscriber],
  )

  const toggleSelection = () => {
    if (!canUserFollowBundlePrograms) return

    if (programBundle.programs?.length !== programBundle.programs?.filter((p) => p.followedInBundle).length) {
      followProgamsInProgramBundle(programBundle.programs?.map((program) => program.id) || [])
    } else {
      unFollowProgamsInProgramBundle(programBundle.programs?.map((program) => program.id) || [])
    }
  }

  if (isLoading) return <LoadingSpinner />

  return (
    <div className={cn("flex w-full flex-col gap-4 px-5 pt-16 pb-5", className)} {...props}>
      {isAuthor ? (
        <div className="flex flex-row flex-wrap gap-3">
          {programBundle.programs?.map((program) => (
            <HuProgramMiniCards key={program.id} item={program} editionAllowed />
          ))}
        </div>
      ) : (
        <div className="my-3 flex flex-col gap-4">
          {canUserFollowBundlePrograms && (
            <>
              <Text fontStyle="BS" className="text-center">
                {word("programBundle.follow.description")}
              </Text>

              <div className="flex flex-row items-center justify-end gap-2">
                <Text fontStyle="BS">
                  {`${programBundle.programs?.filter((p) => p.followedInBundle).length} / ${programBundle.programs?.length} ${word(
                    "global.selected",
                    {
                      count: programBundle.programs?.filter((p) => p.followedInBundle).length,
                    },
                  )}`}
                </Text>
                <Button variant="underline" size="sm" className="px-3 py-2" onClick={toggleSelection}>
                  {programBundle.programs?.length !== programBundle.programs?.filter((p) => p.followedInBundle).length
                    ? word("global.filters.selectAll")
                    : word("global.filters.unselectAll")}
                </Button>
              </div>
            </>
          )}

          {programBundle.programs?.map((program) => (
            <HuProgramBundleMiniCard
              key={program.id}
              programId={program.id}
              followProgamsInProgramBundle={followProgamsInProgramBundle}
              unFollowProgamsInProgramBundle={unFollowProgamsInProgramBundle}
              isFollowed={
                programBundleSubscriber?.programBundle?.programs?.find((p) => p.id === program.id)?.followedInBundle
              }
              canUserFollowBundlePrograms={canUserFollowBundlePrograms ?? false}
            />
          ))}
        </div>
      )}
    </div>
  )
}
